<script setup lang="ts">
import { computed, PropType } from 'vue'
import LibraryCard from './LibraryCard.vue'
import RemoveResourceButton from './RemoveResourceButton.vue'
import { CONTENT_TYPES } from '@/util/access_visibility'
import DynamicHeading from '@/components/DynamicHeading.vue'
import CardContributor from './CardContributor.vue'
import { useContributorVisibility } from './composables/useContributorVisibility'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { LibraryContentObject, LibraryImpactOfDisability } from '@/library/library_types'
import { getImpactOfDisabilityUrl } from '@/util/links'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { useLibraryStore } from '@/library/library.store'

const props = defineProps({
  savedStatement: {
    type: Object as PropType<LibraryContentObject>,
    required: true
  },
  removable: {
    type: Boolean,
    default: true
  },
  clickable: {
    type: Boolean,
    default: true
  },
  headingLevel: {
    type: Number,
    default: 3
  },
  shared: {
    type: Boolean,
    default: false
  }
})

const { contributorIsVisible } = useContributorVisibility(props.savedStatement.content_uuid)

const visibilityStore = useVisibilityStore()
const libraryStore = useLibraryStore()
const analytics = getAnalyticsTracker()
const betaVisibility = computed(() => visibilityStore.betaVisibility)

const resourceType = CONTENT_TYPES.SAVED_IMPACT_OF_DISABILITY

const iodContentBody = computed(() => {
  return props.savedStatement.content_body as LibraryImpactOfDisability
})

const iodHeading = computed(() => {
  const disability = iodContentBody.value?.disability || 'Impact of Disability'
  return disability
})

const subjects = computed(() => {
  return iodContentBody.value?.subjects || []
})

const subjectsText = computed(() => {
  if (subjects.value.length === 0) {
    return 'No subjects specified'
  }
  return subjects.value.join(', ')
})

const iodUrl = computed(() => {
  return getImpactOfDisabilityUrl({
    id: props.savedStatement.content_uuid,
    betaVisibility: betaVisibility.value,
    isShared: props.shared
  })
})

const handleRemoveEvent = () => {
  if (iodContentBody.value) {
    const userFolderRole = libraryStore.getUserRoleForFolder(props.savedStatement.collection_uuid || '')

    analytics.sendEvent(
      EVENT_NAMES.IMPACT_OF_DISABILITY.REMOVED_STATEMENT,
      {
        impact_statement_is_library: true,
        iod_selected_disability: iodContentBody.value.disability,
        iod_selected_subjects: iodContentBody.value.subjects,
        iod_selected_impact_areas: iodContentBody.value.impact_areas?.map(ia => ia.area_label) || [],
        iod_page: 'Library Folder',
        user_folder_role: userFolderRole,
      }
    )
  }
}
</script>

<template>
  <div
    class="relative [&_.remove-resource-button]:hover:opacity-100 [&_.remove-resource-button]:focus:opacity-100 [&_.card-title]:hover:text-gray-1000 [&_.card-title]:focus:text-gray-1000"
  >
    <LibraryCard
      :content-uuid="savedStatement.content_uuid"
      :show-folder-name="savedStatement.show_folder_name"
      :folder-name="savedStatement.collection_body?.name"
      :url="iodUrl"
      :updated-time="iodContentBody?.updated_at"
      v-bind:clickable="clickable"
    >
      <DynamicHeading
        :headingLevel="props.headingLevel"
        :class="`header-sm ${contributorIsVisible ? 'pb-0' : 'pb-3'} text-xl text-primary-500 card-title fs-exclude leading-tight`"
        data-cy="impact-of-disability-title"
      >
        {{ iodHeading }}
      </DynamicHeading>
      <CardContributor
        v-if="!shared"
        :contentId="savedStatement.content_uuid"
      />
      <p
        class="min-h-11 overflow-hidden text-ellipsis text-md [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [display:-webkit-box]"
      >
        <span class="font-medium">Impacted Areas: </span>{{ subjectsText }}
      </p>
    </LibraryCard>
    <RemoveResourceButton
      v-if="!shared && removable"
      :resource="savedStatement.content_uuid"
      :collection="savedStatement.collection_uuid"
      :type="resourceType"
      :ariaLabel="`Remove Impact of Disability statement from collection`"
      :onRemoveCallback="handleRemoveEvent"
    />
  </div>
</template>
